import { SettingsPermissions } from "@redotech/account-common/permissions";
import { hsaPayoutReportEmailsFlag } from "@redotech/redo-conversion-settings-common/hsa-payout-report-emails-flag";
import { directMerchantAccess } from "../../settings-catalog/access";
import type {
  SettingGroupDefinition,
  SettingOwner,
  SettingRisk,
} from "../../settings-catalog/types";

export type HsaFlatTeamSettingGroupId = "hsa.buy_buttons" | "hsa.payout_report";

const hsaSettingsOwner: SettingOwner = {
  kind: "teamSettings",
  path: "settings.hsa",
  product: "payments",
};

const hsaCheckoutRisk: SettingRisk = {
  level: "medium",
  reason:
    "Changes what shoppers see at checkout when they use a Buy with HSA/FSA button.",
};

// NOTE: no availability condition yet — the page's nav gate
// (`team.settings.hsa.enabled`) has no catalog evaluator; needs a new one.
export const HsaFlatTeamSettingGroups = [
  {
    id: "hsa.buy_buttons",
    title: "Buy with HSA buttons",
    description:
      "Behavior of storefront Buy with HSA/FSA buttons that call the Redo HSA window API (window.RedoHsa). Only affects checkouts entered through those buttons.",
    productArea: "payments",
    access: directMerchantAccess(
      SettingsPermissions.SET_CHECKOUT_OPTIMIZATION_SETTINGS,
    ),
    owner: hsaSettingsOwner,
    risk: hsaCheckoutRisk,
    agentEditability: { mode: "requiresApproval" },
    fullPageTarget: { type: "settings", path: "payments/hsa" },
    synonyms: [
      "hsa buy button",
      "buy with hsa",
      "hsa fsa checkout button",
      "redo hsa window api",
    ],
    changeGuidance: {
      summary:
        "Confirm before changing how HSA buy-button checkouts present payment methods.",
    },
  },
  // Its values live in CockroachDB (hsa_payout_report_settings), not on the
  // team, so its settings are cataloged in `catalog.ts` with a `crdb` value
  // source: navigable and highlightable, never read or written by the agent.
  {
    id: "hsa.payout_report",
    title: "Automatic payout reports",
    description:
      "Whether a CSV of each HSA/FSA payout is emailed the day it is deposited, who receives it, and which columns it carries. Reports go out only while the switch is on and at least one recipient is listed.",
    productArea: "payments",
    access: directMerchantAccess(
      SettingsPermissions.SET_CHECKOUT_OPTIMIZATION_SETTINGS,
    ),
    owner: {
      kind: "crdb",
      path: "hsa_payout_report_settings",
      product: "payments",
    },
    risk: {
      level: "medium",
      reason:
        "Settlement data for every payout is emailed to the addresses on the list.",
    },
    agentEditability: { mode: "requiresApproval" },
    availability: {
      conditions: [
        {
          type: "evaluator",
          evaluator: "featureFlag.enabled",
          featureFlag: hsaPayoutReportEmailsFlag.key,
        },
      ],
      unavailableBehavior: "hidden",
      reason:
        "Shown only to teams whose hsa-payout-report-emails flag is on, until the feature launches.",
    },
    fullPageTarget: { type: "settings", path: "payments/hsa" },
    synonyms: [
      "payout report",
      "payout csv email",
      "hsa payout email",
      "finance payout recipients",
      "reconciliation email",
    ],
    changeGuidance: {
      summary:
        "Confirm the recipient list with the merchant before changing where payout reports are sent.",
    },
  },
] as const satisfies readonly SettingGroupDefinition<HsaFlatTeamSettingGroupId>[];

export const HsaFlatTeamSettingGroupById: Record<
  HsaFlatTeamSettingGroupId,
  SettingGroupDefinition<HsaFlatTeamSettingGroupId>
> = (() => {
  const map: Partial<
    Record<
      HsaFlatTeamSettingGroupId,
      SettingGroupDefinition<HsaFlatTeamSettingGroupId>
    >
  > = {};
  for (const group of HsaFlatTeamSettingGroups) {
    map[group.id] = group;
  }
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  return map as Record<
    HsaFlatTeamSettingGroupId,
    SettingGroupDefinition<HsaFlatTeamSettingGroupId>
  >;
})();
